import { apiInstance } from '@/api/instance';

/** Contract: bodyprint-api/docs/external-apis/partners-contract.md (Eventos). */
export type PartnerEventReviewStatus = 'draft' | 'in_review' | 'approved' | 'rejected';

/** `canceled` filters by `canceledAt`; a review status matches only live events. */
export type PartnerEventStatusFilter = PartnerEventReviewStatus | 'canceled';

export type EventRecurrenceFrequency = 'daily' | 'weekly' | 'monthly';

export interface PartnerEventAddress {
  id: string;
  street: string;
  number: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string;
  state: string;
  country: string;
  postalCode: string | null;
  /** Decimal serialized as a string; `null` when the address was not located. */
  latitude: string | null;
  longitude: string | null;
}

export interface PartnerEvent {
  id: string;
  name: string;
  description: string | null;
  bannerUrl: string | null;
  eventType: string | null;
  /** ISO date; the civil date is the first 10 characters. */
  startDate: string;
  startTime: string | null;
  recurrenceFreq: EventRecurrenceFrequency | null;
  recurrenceWeekdays: number[];
  recurrenceUntil: string | null;
  externalUrl: string | null;
  partnerId: string;
  reviewStatus: PartnerEventReviewStatus;
  submittedAt: string | null;
  approvedAt: string | null;
  canceledAt: string | null;
  createdAt: string;
  updatedAt: string;
  addresses: PartnerEventAddress[];
  /** `YYYY-MM-DD`, or `null` when every occurrence is in the past. */
  nextOccurrence: string | null;
  locationResolved: boolean;
  nextOccurrenceIntentsCount: number;
}

export interface PartnerEventReview {
  id: string;
  status: PartnerEventReviewStatus;
  note: string | null;
  createdAt: string;
  decidedByBodyprint: boolean;
}

export interface PartnerEventDetail extends PartnerEvent {
  upcomingOccurrences: string[];
  upcomingIntentsCount: number;
  lastReviewNote: string | null;
  reviews: PartnerEventReview[];
}

export type PartnerEventStatusCounts = Record<PartnerEventStatusFilter | 'all', number>;

export interface PartnerEventsListResponse {
  items: PartnerEvent[];
  count: number;
  statusCounts: PartnerEventStatusCounts;
}

export interface PartnerEventsListParams {
  status?: PartnerEventStatusFilter;
  offset?: number;
  limit?: number;
}

export type PartnerEventAddressBody = Pick<PartnerEventAddress, 'street' | 'city' | 'state'> &
  Partial<
    Pick<PartnerEventAddress, 'number' | 'complement' | 'neighborhood' | 'country' | 'postalCode'>
  >;

/** Body of POST and PUT. */
export type PartnerEventBody = Pick<PartnerEvent, 'name' | 'startDate'> &
  Partial<
    Pick<
      PartnerEvent,
      | 'description'
      | 'bannerUrl'
      | 'eventType'
      | 'startTime'
      | 'recurrenceFreq'
      | 'recurrenceWeekdays'
      | 'recurrenceUntil'
      | 'externalUrl'
    >
  > & { address: PartnerEventAddressBody };

export interface PartnerEventResponse {
  event: PartnerEventDetail;
}

export const partnersEventsApi = {
  list: (params: PartnerEventsListParams) =>
    apiInstance.get<PartnerEventsListResponse>('partners/events', { params }),
  read: (id: string) => apiInstance.get<PartnerEventResponse>(`partners/events/${id}`),
  create: (data: PartnerEventBody) =>
    apiInstance.post<PartnerEventResponse>('partners/events', data),
  update: (id: string, data: PartnerEventBody) =>
    apiInstance.put<PartnerEventResponse>(`partners/events/${id}`, data),
  submit: (id: string) => apiInstance.post<PartnerEventResponse>(`partners/events/${id}/submit`),
  cancel: (id: string) => apiInstance.post<PartnerEventResponse>(`partners/events/${id}/cancel`),
  remove: (id: string) => apiInstance.delete<void>(`partners/events/${id}`),
};

export const partnersEventsApiKeys = {
  all: ['partners', 'events'] as const,
  list: (params: PartnerEventsListParams) => ['partners', 'events', 'list', params] as const,
  detail: (id: string) => ['partners', 'events', 'detail', id] as const,
};
